import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, PartyPopper } from "lucide-react";
import { Link, useRoute } from "wouter";
import { MilestoneExpectedList } from "@/components/portal/milestone-expected-list";
import { MilestoneNotExpectedPills } from "@/components/portal/milestone-not-expected-pills";
import { MilestoneProgressBar } from "@/components/portal/milestone-progress-bar";
import { usePreviewPortal } from "@/components/portal/preview-context";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { extractPortalErrorCode, portalRpc } from "@/lib/portal-rpc";
import { PortalLayout } from "./portal-layout";
import { PortalShell } from "./portal-shell";

type AssignedResource = {
  resourceId: string;
  title: string;
  renderType: string | null;
  behavior: string;
  sourceUrl: string | null;
  opened: boolean;
};

type DueTodo = {
  todoId: string;
  title: string;
  description: string | null;
  link: string | null;
  done: boolean;
  targetDate: string | null;
};

type MilestoneData = {
  onboardingId: string;
  milestoneId: string;
  day: number;
  label: string;
  goal: string;
  readOnly: boolean;
  expected: { index: number; title: string; description: string | null; checked: boolean }[];
  notExpected: string[];
  progress: { done: number; total: number };
  assignedResources?: AssignedResource[];
  dueTodos?: DueTodo[];
};

type MilestoneBodyProps = {
  data: MilestoneData;
  showReadOnlyBadge?: boolean;
  onToggle: (args: {
    onboardingId: string;
    milestoneId: string;
    itemIndex: number;
    checked: boolean;
  }) => void;
};

function AssignedResourceRow({ resource }: { resource: AssignedResource }) {
  // Todo-resources surface inline as plain items (same as dueTodos). They have
  // no content page to navigate to — the todo metadata lives on the row itself.
  if (resource.renderType === "todo") {
    return (
      <div className="flex items-center justify-between rounded-lg border bg-card px-4 py-3">
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{resource.title}</div>
        </div>
        <span
          className={
            resource.opened
              ? "h-2 w-2 rounded-full bg-[hsl(var(--forest-pulse))]"
              : "h-2 w-2 rounded-full bg-muted-foreground/40"
          }
          aria-hidden
        />
      </div>
    );
  }

  const isRedirect = resource.behavior === "redirect" && resource.sourceUrl;
  const inner = (
    <div className="flex items-center justify-between rounded-lg border bg-card px-4 py-3 hover:bg-accent/40">
      <div className="min-w-0">
        <div className="text-sm font-medium truncate">{resource.title}</div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {resource.opened ? "Aberto" : "Não aberto"}
        </div>
      </div>
      <span
        className={
          resource.opened
            ? "h-2 w-2 rounded-full bg-[hsl(var(--forest-pulse))]"
            : "h-2 w-2 rounded-full bg-muted-foreground/40"
        }
        aria-hidden
      />
    </div>
  );
  if (isRedirect) {
    return (
      <a href={resource.sourceUrl ?? "#"} target="_blank" rel="noreferrer" className="block">
        {inner}
      </a>
    );
  }
  return <Link href={`/onboarding/content/${resource.resourceId}`}>{inner}</Link>;
}

export function MilestoneBody({ data, showReadOnlyBadge, onToggle }: MilestoneBodyProps) {
  const fullyDone = data.progress.total > 0 && data.progress.done === data.progress.total;
  const assignedResources = data.assignedResources ?? [];
  const dueTodos = data.dueTodos ?? [];
  return (
    <div className="space-y-6">
      <header className="space-y-3">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Dia {data.day}</div>
        <h1 className="text-3xl font-semibold tracking-tight">{data.label}</h1>
        {showReadOnlyBadge && (
          <div className="text-xs text-muted-foreground">
            Somente leitura · onboarding finalizado
          </div>
        )}
        <MilestoneProgressBar done={data.progress.done} total={data.progress.total} />
      </header>

      {fullyDone && !data.readOnly && (
        <div className="flex items-center gap-2 rounded-lg border border-[hsl(var(--sand-divider))] bg-[hsl(var(--sand-step))] px-4 py-3 text-sm text-[hsl(var(--forest-pulse))]">
          <PartyPopper className="h-4 w-4" />
          Você completou tudo que era esperado para o Dia {data.day}.
        </div>
      )}

      {data.goal && (
        <Card className="p-5">
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
            Objetivo
          </div>
          <p className="text-sm mt-2 leading-relaxed">{data.goal}</p>
        </Card>
      )}

      {(assignedResources.length > 0 || dueTodos.length > 0) && (
        <section className="space-y-2">
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
            Para o Dia {data.day}
          </div>
          <div className="space-y-2">
            {assignedResources.map((r) => (
              <AssignedResourceRow key={r.resourceId} resource={r} />
            ))}
            {dueTodos.map((t) => (
              <div
                key={t.todoId}
                className="flex items-center justify-between rounded-lg border bg-card px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{t.title}</div>
                  {t.description && (
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">
                      {t.description}
                    </div>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t.done ? "Concluído" : "Pendente"}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {data.expected.length > 0 ? (
        <section className="space-y-2">
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
            Esperado até o Dia {data.day}
          </div>
          <MilestoneExpectedList
            items={data.expected}
            readOnly={data.readOnly}
            onToggle={(itemIndex, checked) =>
              onToggle({
                onboardingId: data.onboardingId,
                milestoneId: data.milestoneId,
                itemIndex,
                checked,
              })
            }
          />
        </section>
      ) : null}

      <MilestoneNotExpectedPills items={data.notExpected} />
    </div>
  );
}

function MilestoneInner({ day }: { day: number }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const queryOpts = portalRpc.getMilestone.queryOptions({ input: { day } });
  const milestone = useQuery(queryOpts);
  const milestoneKey = queryOpts.queryKey;

  const toggleItem = useMutation({
    ...portalRpc.toggleMilestoneItem.mutationOptions(),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: milestoneKey });
      const prev = queryClient.getQueryData<typeof milestone.data>(milestoneKey);
      if (prev) {
        const nextExpected = prev.expected.map((it) =>
          it.index === vars.itemIndex ? { ...it, checked: vars.checked } : it,
        );
        queryClient.setQueryData(milestoneKey, {
          ...prev,
          expected: nextExpected,
          progress: {
            ...prev.progress,
            done: nextExpected.filter((it) => it.checked).length,
          },
        });
      }
      return { prev };
    },
    onSuccess: (data) => {
      if (data.ok && data.reachedFullCompletion) {
        toast({
          title: "🎉 Marco alcançado!",
          description: `Dia ${day} concluído.`,
        });
      }
    },
    onError: (_e, _v, ctxPrev) => {
      if (ctxPrev?.prev) queryClient.setQueryData(milestoneKey, ctxPrev.prev);
      toast({ title: "Não foi possível atualizar.", variant: "destructive" });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: milestoneKey });
      queryClient.invalidateQueries({ queryKey: portalRpc.getToday.queryOptions().queryKey });
    },
  });

  if (milestone.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (milestone.error) {
    const code = extractPortalErrorCode(milestone.error);
    if (code === "milestone_not_found") {
      return (
        <Card className="p-8 text-center space-y-1">
          <p className="text-sm font-medium">Sem marco para o Dia {day}</p>
          <p className="text-xs text-muted-foreground">
            Confira na sidebar os dias que existem no seu template.
          </p>
        </Card>
      );
    }
    return (
      <Card className="p-6">
        <p className="text-sm text-muted-foreground">Não foi possível carregar este milestone.</p>
      </Card>
    );
  }

  const data = milestone.data!;
  return (
    <MilestoneBody
      data={data}
      showReadOnlyBadge={data.readOnly}
      onToggle={(args) => toggleItem.mutate(args)}
    />
  );
}

const NOOP = () => {};

export default function PortalMilestone() {
  const [, params] = useRoute<{ day: string }>("/onboarding/milestone/:day");
  const preview = usePreviewPortal();
  const day = Number.parseInt(params?.day ?? "", 10);

  if (preview) {
    return <MilestoneBody data={preview.milestone} showReadOnlyBadge={false} onToggle={NOOP} />;
  }
  return (
    <PortalShell>
      <PortalLayout>
        {Number.isFinite(day) ? (
          <MilestoneInner day={day} />
        ) : (
          <Card className="p-6">
            <p className="text-sm text-muted-foreground">Milestone inválido.</p>
          </Card>
        )}
      </PortalLayout>
    </PortalShell>
  );
}
